import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const STRATEGY_LABELS = {
  PYRAMID_HEDGING:             "Pyramid Hedging",
  DELTA_HEDGED_SHORT_STRANGLE: "DHSS",
  FUTURES_PYRAMID_HEDGING:     "Futures Pyramid Hedging (FPH)",
  WEEKLY_INDEX_SHORT_STRADDLE: "Weekly Index Short Straddle (WISS)",
};

const GUARDRAIL_FIELDS = [
  { key: "max_concurrent_positions",  label: "Max concurrent positions" },
  { key: "max_daily_actions",         label: "Max auto-approved actions / day" },
  { key: "consecutive_failure_limit", label: "Consecutive-failure limit (per symbol)" },
  { key: "daily_loss_limit",          label: "Daily loss limit (% of deployed capital) — only gates NEW-exposure actions, never rolls/closes/stop-losses" },
  { key: "price_sanity_pct",          label: "Price sanity band (%)" },
  { key: "stale_data_max_age_seconds", label: "Stale-data max age (s)" },
];

export default function AutomationPage() {
  const [settings, setSettings]     = useState([]);
  const [guardrails, setGuardrails] = useState([]);
  const [killSwitch, setKillSwitch] = useState(false);
  const [pending, setPending]       = useState([]);
  const [heartbeats, setHeartbeats] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [msg, setMsg]               = useState(null);
  const [guardrailDrafts, setGuardrailDrafts] = useState({}); // {strategy_type: {field: value}}
  const [savingGuardrail, setSavingGuardrail] = useState(null);

  const flash = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/automation");
      setSettings(res.data.settings || []);
      setGuardrails(res.data.guardrails || []);
      setKillSwitch(!!res.data.kill_switch);
      setPending(res.data.pending_patterns || []);
      setHeartbeats(res.data.heartbeats || []);
      const drafts = {};
      (res.data.guardrails || []).forEach(g => { drafts[g.strategy_type] = { ...g }; });
      setGuardrailDrafts(drafts);
    } catch {
      flash("Failed to load automation settings.", false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleStrategy = async (strategyType, enabled) => {
    try {
      await axios.post("/admin/automation/toggle", { strategy_type: strategyType, enabled });
      flash(`${STRATEGY_LABELS[strategyType] || strategyType} auto-execute ${enabled ? "enabled" : "disabled"}.`);
      fetchAll();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update toggle.", false);
    }
  };

  const toggleKillSwitch = async (enabled) => {
    if (enabled && !window.confirm(
      "This forces EVERY strategy back to manual approval immediately, " +
      "regardless of their individual toggles. Continue?"
    )) return;
    try {
      await axios.post("/admin/automation/kill-switch", { enabled });
      flash(enabled ? "Kill switch ENGAGED — all auto-execution halted." : "Kill switch released.");
      fetchAll();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update kill switch (superadmin only).", false);
    }
  };

  const saveGuardrails = async (strategyType) => {
    setSavingGuardrail(strategyType);
    try {
      const draft = guardrailDrafts[strategyType] || {};
      const payload = { strategy_type: strategyType };
      GUARDRAIL_FIELDS.forEach(f => { payload[f.key] = draft[f.key] === "" ? null : draft[f.key]; });
      await axios.post("/admin/automation/guardrails", payload);
      flash(`Guardrails updated for ${STRATEGY_LABELS[strategyType] || strategyType}.`);
      fetchAll();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to update guardrails.", false);
    } finally {
      setSavingGuardrail(null);
    }
  };

  const reviewPattern = async (id, approve) => {
    try {
      await axios.post(`/admin/automation/patterns/${id}/review`, { approve });
      flash(approve ? "Pattern approved — future matches will auto-execute." : "Pattern rejected.");
      fetchAll();
    } catch (e) {
      flash(e?.response?.data?.error || "Failed to review pattern.", false);
    }
  };

  const strategyTypes = settings.filter(s => s.strategy_type !== "__GLOBAL__");

  return (
    <div style={{ padding: "20px 25px 40px", background: "#f4f6f9", minHeight: "100vh" }}>
      {msg && <div style={{ ...toast, background: msg.ok ? "#28a745" : "#dc3545" }}>{msg.text}</div>}

      <div style={{ marginBottom: 20 }}>
        <h2 style={pageTitle}>Automation</h2>
        <p style={{ color: "#888", fontSize: 13, margin: 0 }}>
          Auto-approval of rule-engine suggestions, guardrails, and the novelty gate.
          See docs/MULTI_ASSET_AUTOTRADE_EXPANSION_PLAN.md §9. Every strategy defaults to
          manual approval (today's behavior) until switched on below.
        </p>
      </div>

      {/* Kill switch */}
      <div style={{ ...tile, marginBottom: 20, border: killSwitch ? "2px solid #dc3545" : "1px solid #f1c40f" }}>
        <div style={sectionTitle}>Global Kill Switch (superadmin only)</div>
        <div style={sectionHint}>
          Forces every strategy back to manual approval instantly, regardless of the per-strategy
          toggles below. Use this first if something looks wrong.
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
          <input type="checkbox" checked={killSwitch} onChange={e => toggleKillSwitch(e.target.checked)} style={{ width: 18, height: 18 }} />
          <span style={{ fontWeight: 700, color: killSwitch ? "#dc3545" : "#333" }}>
            {killSwitch ? "ENGAGED — all auto-execution halted" : "Off — per-strategy toggles apply normally"}
          </span>
        </label>
      </div>

      {/* Per-strategy toggles + guardrails */}
      {strategyTypes.map(s => {
        const draft = guardrailDrafts[s.strategy_type] || {};
        return (
          <div key={s.strategy_type} style={{ ...tile, marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={sectionTitle}>{STRATEGY_LABELS[s.strategy_type] || s.strategy_type}</div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={!!s.auto_execute_enabled}
                  disabled={loading}
                  onChange={e => toggleStrategy(s.strategy_type, e.target.checked)}
                  style={{ width: 16, height: 16 }}
                />
                <span style={{ fontSize: 13, fontWeight: 600, color: s.auto_execute_enabled ? "#28a745" : "#6c757d" }}>
                  {s.auto_execute_enabled ? "Auto-execute ON" : "Manual approval"}
                </span>
              </label>
            </div>
            <div style={sectionHint}>
              Even with this ON, a brand-new opportunity pattern (new symbol for this strategy)
              still holds for review below — see "Pending pattern reviews."
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
              {GUARDRAIL_FIELDS.map(f => (
                <div key={f.key}>
                  <div style={{ fontSize: 11, color: "#6c757d", marginBottom: 3 }}>{f.label}</div>
                  <input
                    type="number"
                    value={draft[f.key] ?? ""}
                    onChange={e => setGuardrailDrafts(prev => ({
                      ...prev,
                      [s.strategy_type]: { ...prev[s.strategy_type], [f.key]: e.target.value },
                    }))}
                    style={{ ...smallInput, width: "100%" }}
                  />
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10 }}>
              <button
                type="button"
                onClick={() => saveGuardrails(s.strategy_type)}
                disabled={savingGuardrail === s.strategy_type}
                style={btnPrimary}
              >
                {savingGuardrail === s.strategy_type ? "Saving…" : "Save Guardrails"}
              </button>
            </div>
          </div>
        );
      })}

      {/* Pending pattern reviews */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>Pending Pattern Reviews (Novelty Gate)</div>
        <div style={sectionHint}>
          A new kind of opportunity holds here even with auto-execute ON for its strategy.
          Approve to let future matches auto-execute; reject to keep holding them for manual review.
        </div>
        {pending.length === 0 ? (
          <div style={{ fontSize: 13, color: "#888" }}>Nothing pending review.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#6c757d", fontSize: 11 }}>
                <th style={th}>Strategy</th><th style={th}>Pattern</th><th style={th}>First Seen</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pending.map(p => (
                <tr key={p.id} style={{ borderTop: "1px solid #f1f1f1" }}>
                  <td style={td}>{STRATEGY_LABELS[p.strategy_type] || p.strategy_type}</td>
                  <td style={{ ...td, fontFamily: "monospace" }}>{p.pattern_key}</td>
                  <td style={td}>{p.first_seen_at}</td>
                  <td style={td}>
                    <button type="button" onClick={() => reviewPattern(p.id, true)} style={{ ...btnPrimary, marginRight: 6, padding: "5px 12px" }}>Approve</button>
                    <button type="button" onClick={() => reviewPattern(p.id, false)} style={{ ...btnSecondary, padding: "5px 12px" }}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Health watchdogs */}
      <div style={{ ...tile, marginBottom: 20 }}>
        <div style={sectionTitle}>System Health Watchdogs</div>
        <div style={sectionHint}>Last known-good check per watched component.</div>
        {heartbeats.length === 0 ? (
          <div style={{ fontSize: 13, color: "#888" }}>No heartbeats recorded yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "#6c757d", fontSize: 11 }}>
                <th style={th}>Check</th><th style={th}>Last OK</th><th style={th}>Last Error</th>
              </tr>
            </thead>
            <tbody>
              {heartbeats.map(h => (
                <tr key={h.check_name} style={{ borderTop: "1px solid #f1f1f1" }}>
                  <td style={{ ...td, fontFamily: "monospace" }}>{h.check_name}</td>
                  <td style={td}>{h.last_ok_at || "—"}</td>
                  <td style={{ ...td, color: h.last_error ? "#dc3545" : "#888" }}>{h.last_error || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── Styles (matches BrandingSettingsPage.jsx conventions) ──────── */

const pageTitle    = { fontSize: 22, fontWeight: 700, color: "#2c3e50", margin: 0 };
const toast        = { position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "10px 20px", borderRadius: 8, color: "#fff", fontWeight: 600, fontSize: 14 };
const tile         = { background: "#fff", borderRadius: 10, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: "#2c3e50", marginBottom: 4 };
const sectionHint  = { fontSize: 12, color: "#888", marginBottom: 12 };
const smallInput   = { padding: "7px 12px", borderRadius: 8, border: "1px solid #dee2e6", fontSize: 13 };
const btnPrimary   = { padding: "8px 18px", borderRadius: 8, border: "none", background: "#2c3e50", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 };
const btnSecondary = { padding: "8px 18px", borderRadius: 8, border: "1px solid #ccc", background: "#fff", color: "#333", cursor: "pointer", fontSize: 13 };
const th           = { padding: "6px 8px" };
const td            = { padding: "6px 8px" };
